import { type ChangeEvent, useCallback, useMemo } from 'react'
import type { FileInfoType } from '../types'
import { useSelectedContext } from './useSelectedContext'

interface UseSelectAllOptions {
  files: FileInfoType[] | undefined
}

interface UseSelectAllReturn {
  checked: boolean | 'mixed'
  onChange: (_: ChangeEvent<HTMLInputElement>, data: { checked?: string | boolean }) => void
}

/**
 * Derives Select All checkbox state from current context selection and handles
 * select/deselect-all interactions across uploaded files only.
 *
 * The working document is always independent — Select All neither selects
 * nor deselects it. Only completed (parsed) files are selectable; pending
 * files are skipped and may cause an indeterminate (mixed) state.
 */
export function useSelectAll({ files }: UseSelectAllOptions): UseSelectAllReturn {
  const { getFileSelection, updateFileSelection } = useSelectedContext()

  const uploadedFiles = useMemo(() => files ?? [], [files])

  const allSelected =
    uploadedFiles.length > 0 &&
    uploadedFiles.every((f) => {
      const sel = getFileSelection(f.id)
      return sel && Array.isArray(sel.sections) && sel.sections.length === 0
    })

  const someSelected =
    !allSelected &&
    uploadedFiles.some((f) => {
      const sel = getFileSelection(f.id)
      return sel && Array.isArray(sel.sections)
    })

  const checked: boolean | 'mixed' = allSelected ? true : someSelected ? 'mixed' : false

  const onChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, data: { checked?: string | boolean }) => {
      const isChecked = data.checked === true || data.checked === 'true'
      if (isChecked) {
        uploadedFiles.forEach((file) => {
          if (file.parsingStatus === 'completed') {
            updateFileSelection({
              id: file.id,
              fileName: file.name,
              extension: file.type,
              sections: [],
            })
          }
        })
      } else {
        uploadedFiles.forEach((file) => {
          updateFileSelection({
            id: file.id,
            fileName: file.name,
            extension: file.type,
            sections: null,
          })
        })
      }
    },
    [uploadedFiles, updateFileSelection]
  )

  return { checked, onChange }
}
